
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { trackFeatureUsage } from "@/utils/monitoring";
import { useFileContent } from "./analysis/useFileContent";
import { useProgressSimulation } from "./analysis/useProgressSimulation";
import { useAnalysisUpdates } from "./analysis/useAnalysisUpdates";
import type { AnalysisResult, AnalysisFunction } from "./analysis/types";

export type { AnalysisResult };

export function useContentAnalysis<T extends object>(
  analyzeFunction: AnalysisFunction<T>,
  formatResults: (result: T) => AnalysisResult,
  errorHandler?: (error: unknown, operation?: string) => void
) {
  const [results, setResults] = useState<AnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { readFileContent } = useFileContent();
  const { progress, setProgress, startProgressSimulation } = useProgressSimulation();
  const { toast } = useToast();

  // Setup real-time updates
  useAnalysisUpdates(
    progress.partialResults?.analysisId,
    formatResults,
    setResults,
    setProgress,
    (title, description, variant) => toast({ title, description, variant })
  );

  const handleAnalyze = async (content: File | string) => {
    try {
      setIsLoading(true);
      setProgress({
        progress: 0,
        status: 'processing',
        partialResults: undefined
      });
      
      let textContent: string;
      
      if (typeof content === 'string') {
        textContent = content;
      } else {
        textContent = await readFileContent(content);
      }
      
      // Set a reasonable analysis size limit for better performance
      const MAX_ANALYSIS_LENGTH = 10000;
      const actualContent = textContent.length > MAX_ANALYSIS_LENGTH ? 
        textContent.substring(0, MAX_ANALYSIS_LENGTH) + "..." : 
        textContent;
      
      trackFeatureUsage("contentAnalysis", {
        contentType: typeof content === 'string' ? 'text' : 'file',
        contentLength: actualContent.length,
        timestamp: new Date().toISOString()
      });
      
      const progressInterval = startProgressSimulation();
      
      try {
        // Set a maximum timeout to prevent endless analysis
        const timeoutPromise = new Promise<T>((_, reject) => {
          setTimeout(() => reject(new Error("Analysis timeout - taking too long")), 10000);
        });
        
        const analysisPromise = analyzeFunction(actualContent) as Promise<T>;
        
        // Race the analysis against the timeout
        const detectionResult = await Promise.race([analysisPromise, timeoutPromise]);
        clearInterval(progressInterval);
        
        const formattedResults = formatResults(detectionResult);
        
        if ('id' in detectionResult) {
          formattedResults.analysisId = (detectionResult as any).id as string;
          setProgress(prev => ({
            ...prev,
            partialResults: formattedResults
          }));
        } else {
          setProgress({
            progress: 100,
            status: 'completed',
            partialResults: undefined
          });
        }
        
        setResults(formattedResults);
        
        toast({
          title: "Analysis Complete",
          description: `Content analysis completed with ${formattedResults.overallStatus} result.`,
          variant: formattedResults.overallStatus === "safe" ? "default" : 
                  formattedResults.overallStatus === "suspicious" ? "default" : "destructive"
        });
        
      } catch (error) {
        clearInterval(progressInterval);
        
        setProgress({
          progress: 100,
          status: 'completed',
          partialResults: undefined
        });
        
        // If it's a timeout error, create a partial result
        if (error instanceof Error && error.message.includes("timeout")) {
          const partialResult = {
            score: 0.5,
            confidence: 0.6,
            flags: ["Analysis timeout", "Partial results"],
            explanation: "Analysis was taking too long. Here are partial results.",
            modelName: "Timeout Handler",
            timestamp: new Date()
          };
          
          setResults(formatResults(partialResult as unknown as T));
          
          toast({
            title: "Analysis Timeout",
            description: "Analysis was taking too long, showing partial results.",
            variant: "default"
          });
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error("Error during analysis:", error);
      
      setProgress({
        progress: 0,
        status: 'error',
        partialResults: undefined
      });
      
      if (errorHandler) {
        errorHandler(error, "contentAnalysis");
      } else {
        toast({
          title: "Analysis Failed",
          description: "There was an error analyzing the content. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetResults = () => {
    setResults(null);
    setProgress({
      progress: 0,
      status: 'idle',
      partialResults: undefined
    });
  };

  return {
    results,
    isLoading,
    progress: progress.progress,
    analysisStatus: progress.status,
    partialResults: progress.partialResults,
    handleAnalyze,
    resetResults
  };
}
